import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../api/axios";
import { toast } from "sonner";
import BottomTabNav from "../components/BottomTabNav";
import { 
  CheckCircle, 
  Calendar, 
  Clock, 
  Video, 
  MessageCircle,
  Copy,
  ExternalLink,
  User,
  AlertCircle,
  Loader2
} from "lucide-react";

const CALL_METHOD_ICONS = {
  zoom: Video,
  google_meet: Video,
  whatsapp: MessageCircle
};

const CALL_METHOD_LABELS = {
  zoom: "Zoom",
  google_meet: "Google Meet",
  whatsapp: "WhatsApp"
};

const BookingConfirmationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const isRTL = i18n.language === 'ar';
  const bookingId = searchParams.get('booking_id');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!bookingId || !sessionId) {
      setError(t('booking.invalidLink'));
      setLoading(false);
      return;
    }
    
    confirmPayment();
  }, [bookingId, sessionId]);

  const confirmPayment = async () => {
    try {
      const response = await api.post(`/bookings/${bookingId}/confirm-payment?session_id=${sessionId}`);
      setBooking(response.data);
      toast.success(t('booking.confirmed'));
    } catch (err) {
      console.error("Payment confirmation failed:", err);
      if (err.response?.status === 400) {
        setError(t('booking.paymentIncomplete'));
      } else {
        setError(t('booking.confirmError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const copyMeetingLink = () => {
    if (booking?.meeting_link) {
      navigator.clipboard.writeText(booking.meeting_link);
      setCopied(true);
      toast.success(t('common.copied'));
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return {
      date: date.toLocaleDateString(i18n.language, { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      time: date.toLocaleTimeString(i18n.language, { 
        hour: '2-digit', 
        minute: '2-digit',
        timeZoneName: 'short'
      })
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen pb-24 p-6 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-2xl mx-auto text-center py-12">
          <Loader2 className="w-16 h-16 text-aviation animate-spin mx-auto mb-4" />
          <p className="text-slate-600">{t('booking.verifyingPayment')}</p>
        </div>
        <BottomTabNav />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pb-24 p-6 bg-gradient-to-b from-slate-50 to-white" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl border-2 border-red-200 shadow-sm p-8 text-center mt-12">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-travel-navy mb-3">
              {t('booking.errorTitle')}
            </h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/agents')}
              className="bg-aviation text-white rounded-full px-8 py-3 font-semibold"
            >
              {t('booking.backToAgents')}
            </button>
          </div>
        </div>
        <BottomTabNav />
      </div>
    );
  }

  const CallMethodIcon = CALL_METHOD_ICONS[booking?.call_method] || Video;
  const dateTime = formatDateTime(booking?.start_time_utc);

  return (
    <div className="min-h-screen pb-24 p-6 bg-gradient-to-b from-emerald-50 to-white" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-2xl mx-auto">
        {/* Success Header */}
        <div className="text-center mb-8 pt-6">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-12 h-12 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-travel-navy mb-2">
            {t('booking.successTitle')}
          </h1>
          <p className="text-slate-600">
            {t('booking.successSubtitle')}
          </p>
        </div>

        {/* Booking Details Card */}
        <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-travel-navy mb-4">
            {t('booking.details')}
          </h2>
          
          {/* Agent Info */}
          <div className="flex items-center gap-3 pb-4 border-b border-slate-100 mb-4">
            <div className="w-12 h-12 bg-aviation/10 rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-aviation" />
            </div>
            <div>
              <p className="font-semibold text-travel-navy">{booking?.agent_name}</p>
              <p className="text-sm text-slate-500">{t('booking.liveAgent')}</p>
            </div>
          </div>

          {/* Date & Time */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-slate-400" />
              <div>
                <p className="font-medium text-travel-navy">{dateTime.date}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-slate-400" />
              <div>
                <p className="font-medium text-travel-navy">
                  {dateTime.time} • {booking?.duration} {t('agents.min')}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <CallMethodIcon className="w-5 h-5 text-slate-400" />
              <p className="font-medium text-travel-navy">
                {CALL_METHOD_LABELS[booking?.call_method]}
              </p>
            </div>
          </div>
        </div>

        {/* Meeting Link Card */}
        <div className="bg-gradient-to-r from-aviation to-sky-500 rounded-2xl p-6 mb-6 text-white">
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            <Video className="w-5 h-5" />
            {t('booking.meetingLink')}
          </h2>
          <p className="text-sm opacity-90 mb-4">
            {t('booking.meetingLinkHint')}
          </p>
          
          <div className="bg-white/20 backdrop-blur rounded-xl p-3 flex items-center gap-2">
            <input
              type="text"
              value={booking?.meeting_link || ''}
              readOnly
              className="flex-1 bg-transparent text-white placeholder-white/70 text-sm outline-none"
            />
            <button
              onClick={copyMeetingLink}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              title={t('common.copy')}
            >
              <Copy className={`w-5 h-5 ${copied ? 'text-emerald-300' : ''}`} />
            </button>
            <a
              href={booking?.meeting_link}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              title={t('common.openLink')}
            >
              <ExternalLink className="w-5 h-5" />
            </a>
          </div>
        </div>

        {/* Intake Notes (if provided) */}
        {booking?.intake_notes && (
          <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-travel-navy mb-3">
              {t('booking.yourNotes')}
            </h2>
            <p className="text-slate-600 bg-slate-50 p-4 rounded-xl">
              {booking.intake_notes}
            </p>
          </div>
        )}

        {/* Payment Info */}
        <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <span className="text-slate-600">{t('booking.amountPaid')}</span>
            <span className="text-xl font-bold text-emerald-600">
              ${booking?.amount?.toFixed(2)} {booking?.currency?.toUpperCase()}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            {t('booking.bookingId')}: {booking?.id}
          </p>
        </div>

        {/* Cancellation Notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-amber-800">
            <strong>{t('booking.cancellationPolicy')}</strong><br />
            {t('booking.cancellationMessage')}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={() => navigate('/profile')}
            className="w-full bg-aviation text-white rounded-full h-12 font-semibold transition-all hover:shadow-lg"
          >
            {t('booking.viewMyBookings')}
          </button>
          <button
            onClick={() => navigate('/agents')}
            className="w-full bg-white border-2 border-slate-200 text-slate-700 rounded-full h-12 font-semibold transition-all hover:bg-slate-50"
          >
            {t('booking.browseMoreAgents')}
          </button>
        </div>
      </div>

      <BottomTabNav />
    </div>
  );
};

export default BookingConfirmationPage;
